import { useCallback, useEffect, useState } from 'react';

interface RecentLoan {
  id: string;
  loanNumber: string;
  borrowerName: string;
  status: string;
  viewedAt: number;
}

const STORAGE_KEY = 'lm:recent-loans';
const MAX_RECENT = 8;

function loadRecent(): RecentLoan[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function saveRecent(loans: RecentLoan[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(loans));
}

/**
 * Track and retrieve recently viewed loans.
 * Call `trackView(...)` when a loan detail page is opened.
 */
export function useRecentLoans() {
  const [recent, setRecent] = useState<RecentLoan[]>(loadRecent);

  // Reload if another tab updates localStorage
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setRecent(loadRecent());
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const trackView = useCallback(
    (loan: { id: string; loanNumber: string; borrowerName: string; status: string }) => {
      const now = Date.now();
      const updated = [
        { ...loan, viewedAt: now },
        ...loadRecent().filter(r => r.id !== loan.id),
      ].slice(0, MAX_RECENT);
      saveRecent(updated);
      setRecent(updated);
    },
    [],
  );

  const clearRecent = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setRecent([]);
  }, []);

  return { recent, trackView, clearRecent };
}
